"use client";

import { Box, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { passwordStrength } from "check-password-strength";

export type PasswordStrengthId = 0 | 1 | 2 | 3;

const DEFAULT_PASSWORD_STRENGTH_METER_LABELS = [
  "Weak",
  "Fair",
  "Strong",
  "Very Strong",
] as string[];

export interface PasswordStrengthMeterProps {
  value: string;
  /** Overrides every visible string. Defaults to English. */
  labels?: string[];
  emptyLabel?: string;
  dataCy?: string;
  className?: string;
}

const fillTrackFor = (
  strengthId: PasswordStrengthId,
  theme: Theme
): { rest: string; hover: string; active: string } => {
  const rmh = theme.vars!.palette.rmh;
  if (strengthId <= 0) return rmh.paper["8-errorFilled"];
  if (strengthId === 1) return rmh.paper["12-warningFilled"];
  if (strengthId === 2) return rmh.paper["5-brandFilled"];
  return rmh.paper["10-successFilled"];
};

export const PasswordStrengthMeter = ({
  value,
  emptyLabel = "Enter a password",
  labels = DEFAULT_PASSWORD_STRENGTH_METER_LABELS,
  dataCy = "password_strength_meter",
  className,
}: PasswordStrengthMeterProps) => {
  const isEmpty = value.length === 0;
  const strengthId = isEmpty
    ? null
    : (passwordStrength(value).id as PasswordStrengthId);
  const label = strengthId === null ? emptyLabel : labels[strengthId];

  return (
    <Stack spacing={2} className={className} data-cy={dataCy}>
      <Stack direction="row" spacing={0.5}>
        {Array.from({ length: labels.length }, (_, i) => {
          const isFilled = strengthId !== null && i <= strengthId;
          return (
            <Box
              key={i}
              sx={(theme) => {
                const fillTrack =
                  strengthId === null ? null : fillTrackFor(strengthId, theme);
                return {
                  flex: 1,
                  height: "0.25rem",
                  borderRadius: theme.shape.medium,
                  backgroundColor:
                    isFilled && fillTrack
                      ? fillTrack.rest
                      : theme.vars!.palette.rmh.paper["7-brandFaint"].hover,
                  transition: theme.transitions.create("background-color", {
                    duration: theme.transitions.duration.shortest,
                  }),
                };
              }}
              data-cy={`${dataCy}__bar_${i}`}
            />
          );
        })}
      </Stack>
      <Typography
        variant="inputLabelMd"
        color="text.secondary"
        sx={{ textAlign: "right" }}
        data-cy={`${dataCy}__label`}
      >
        {label}
      </Typography>
    </Stack>
  );
};
